import { createRouter, createWebHistory } from 'vue-router'

import { generateRoutes } from '@/extensions/langRouter'
import { baseRouteName, localizedRouteName, ROUTES } from '@/helpers'
import store from '@/store'

const routes = [
  {
    path: '/',
    name: ROUTES.AUTH.BASE,
    component: () => import('@/views/Auth/Auth.vue'),
    children: [
      {
        path: '/',
        name: ROUTES.HOME,
        redirect: { name: localizedRouteName(ROUTES.HUNT.WELCOME) },
      },
      {
        path: '/bienvenue',
        paths: { en: '/welcome' },
        name: ROUTES.HUNT.WELCOME,
        component: () => import('@/views/Hunt/Welcome.vue'),
        beforeEnter: () => {
          if (store.state.hunter.completed_riddles_count > 0) {
            if (!store.state.riddle.current) {
              return { name: localizedRouteName(ROUTES.HUNT.CONGRATULATIONS) }
            }

            return {
              name: localizedRouteName(ROUTES.HUNT.RIDDLE),
              params: { id: store.state.riddle.current },
            }
          }
        },
        meta: { depth: 1, has_nav: true },
      },
      {
        path: '/enigmes/:id',
        paths: { en: '/riddles/:id' },
        name: ROUTES.HUNT.RIDDLE,
        props: true,
        component: () => import('@/views/Hunt/Riddle.vue'),
        beforeEnter: (to) => {
          if (store.state.riddle.current === null) {
            return { name: localizedRouteName(ROUTES.HUNT.CONGRATULATIONS) }
          }

          if (to.params.id !== store.state.riddle.current.toString()) {
            return {
              name: localizedRouteName(ROUTES.HUNT.RIDDLE),
              params: { id: store.state.riddle.current },
            }
          }
        },
        meta: { depth: 1, has_nav: true },
      },
      {
        path: '/felicitations',
        paths: { en: '/congratulations' },
        name: ROUTES.HUNT.CONGRATULATIONS,
        component: () => import('@/views/Hunt/Congratulations.vue'),
        beforeEnter: () => {
          if (store.state.riddle.current !== null) {
            return { name: localizedRouteName(ROUTES.HUNT.WELCOME) }
          }
        },
        meta: { depth: 1, has_nav: true },
      },
      {
        path: '/avatar',
        paths: { en: '/avatar' },
        name: ROUTES.WARDROBE,
        component: () => import('@/views/Wardrobe.vue'),
        meta: { depth: 2 },
      },
      {
        path: '/:pathMatch(.*)',
        name: 'catch-all-auth',
        redirect: { name: localizedRouteName(ROUTES.HOME) },
      },
    ],
  },
  {
    path: '/confirmation',
    paths: { en: '/confirmation' },
    name: ROUTES.AUTH.CONFIRMATION,
    component: () => import('@/views/Auth/AuthConfirmation.vue'),
    meta: { depth: 1 },
  },
  {
    path: '/participants',
    paths: { en: '/participants' },
    name: ROUTES.AUTH.GROUP_SIZE,
    component: () => import('@/views/Auth/AuthGroupSize.vue'),
    meta: { depth: 1 },
  },
  {
    path: '/connexion',
    paths: { en: '/login' },
    name: ROUTES.AUTH.LOGIN,
    component: () => import('@/views/Auth/AuthLogin.vue'),
    meta: { depth: 1 },
  },
  {
    path: '/options',
    paths: { en: '/options' },
    name: ROUTES.AUTH.OPTIONS,
    component: () => import('@/views/Auth/AuthOptions.vue'),
    meta: { depth: 1 },
  },
  {
    path: '/deconnexion',
    paths: '/logout',
    name: ROUTES.AUTH.LOGOUT,
    beforeEnter: async () => {
      await store.dispatch('logout')

      return { name: localizedRouteName(ROUTES.AUTH.BASE) }
    },
  },
  {
    path: '/politique-de-confidentialite',
    paths: { en: '/privacy' },
    name: ROUTES.TERMS.PRIVACY,
    component: () => import('@/views/Terms/Privacy.vue'),
    meta: { depth: 2 },
  },
  {
    path: '/reglements',
    paths: { en: '/rules' },
    name: ROUTES.TERMS.RULES,
    component: () => import('@/views/Terms/Rules.vue'),
    meta: { depth: 2 },
  },
  {
    path: '/termes-et-conditions',
    paths: { en: '/terms' },
    name: ROUTES.TERMS.TERMS,
    component: () => import('@/views/Terms/Terms.vue'),
    meta: { depth: 2 },
  },
  {
    path: '/:pathMatch(.*)',
    name: 'catch-all',
    redirect: localizedRouteName(ROUTES.AUTH.BASE),
  },
]

const localizedRoutes = generateRoutes(routes, 'fr', 'en')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: '-active',
  linkExactActiveClass: '-exact-active',
  routes: localizedRoutes,
  scrollBehavior(to) {
    return to.hash ? { el: to.hash, behavior: 'smooth' } : { top: 0, behavior: 'smooth' }
  },
})

router.beforeEach(async (to) => {
  if (!store.state.auth.initiated) {
    await store.dispatch('initAuth')
  }

  if (store.state.auth.logged_in) {
    if (!store.state.hunter.has_phone) {
      const localizedAuthOptionsRouteName = localizedRouteName(ROUTES.AUTH.OPTIONS)
      if (to.name !== localizedAuthOptionsRouteName) return { name: localizedAuthOptionsRouteName }
    } else if (!store.state.hunter.group_size) {
      const localizedAuthOptionsRouteName = localizedRouteName(ROUTES.AUTH.GROUP_SIZE)
      if (to.name !== localizedAuthOptionsRouteName) return { name: localizedAuthOptionsRouteName }
    } else {
      const toRouteBaseName = baseRouteName(to.name)
      const localizedWelcomeRouteName = localizedRouteName(ROUTES.HUNT.WELCOME)

      if (
        [
          ROUTES.AUTH.CONFIRMATION,
          ROUTES.AUTH.GROUP_SIZE,
          ROUTES.AUTH.LOGIN,
          ROUTES.AUTH.OPTIONS,
        ].includes(toRouteBaseName) &&
        to.name !== localizedWelcomeRouteName
      ) {
        return { name: localizedWelcomeRouteName }
      }
    }
  }
})

router.afterEach((to, from) => {
  let transition = 'page-slide-left'
  if (from?.meta?.depth === 2 && to?.meta?.depth === 1) {
    transition = 'page-slide-right'
  }

  to.meta.transition = transition
})

export default router
